/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { PLAN_CONSTANTS } from '@touchplan/common';
import { ClipboardDragLine, CLIPBOARD_DRAG_FEEDBACK_SETTINGS } from './ClipboardDragLine';
import * as strings from '@touchplan/strings';

export interface CountOfOffScreenTickets {
	left: number;
	right: number;
}

export class ClipboardPasteDragFeedback {
	protected parentApplication: AbstractApplication;
	protected backgroundContainer: FancyContainer;

	protected rootContainer: FancyContainer;
	protected topLineAndTexts: ClipboardDragLine;
	protected bottomLineAndTexts: ClipboardDragLine;

	protected ticketBoundsHeight: number;

	constructor(parentApplication: AbstractApplication, backgroundContainer: FancyContainer) {
		this.parentApplication = parentApplication;

		this.backgroundContainer = backgroundContainer;

		this.rootContainer = new FancyContainer();
		this.rootContainer.visible = false;
		this.rootContainer.interactiveChildren = false;

		this.topLineAndTexts = new ClipboardDragLine(
			strings.CLIPBOARD_PASTE_TOP_GUIDE_TEXT,
			this.rootContainer,
			parentApplication
		);
		this.bottomLineAndTexts = new ClipboardDragLine(
			strings.CLIPBOARD_PASTE_BOTTOM_GUIDE_TEXT,
			this.rootContainer,
			parentApplication
		);

		this.backgroundContainer.addChild(this.rootContainer);
	}

	public initializeLineData(
		mouseY: number,
		ticketBoundsHeight: number,
		ticketsOffScreen: CountOfOffScreenTickets
	): void {
		this.ticketBoundsHeight = ticketBoundsHeight;
		this.updateGeometry(mouseY, ticketBoundsHeight, ticketsOffScreen);
	}

	public updateLineData(mouseY: number, ticketsOffScreen: CountOfOffScreenTickets): void {
		this.updateGeometry(mouseY, this.ticketBoundsHeight, ticketsOffScreen);
	}

	private updateGeometry(
		mouseY: number,
		ticketBoundsHeight: number,
		ticketsOffScreen: CountOfOffScreenTickets
	): void {
		const sideOffset = PLAN_CONSTANTS.SIDEBAR_WIDTH;
		const screenWidth = this.topLineAndTexts.getScreenWidth();
		const offsetWidth = screenWidth - sideOffset;

		const screenY = mouseY - ticketBoundsHeight;
		const lineHeightAndMargin =
			CLIPBOARD_DRAG_FEEDBACK_SETTINGS.lineHeight + CLIPBOARD_DRAG_FEEDBACK_SETTINGS.lineAndTicketMargin;

		// The X and Y coordinates are relative to the screen:
		this.rootContainer.x = sideOffset;
		this.rootContainer.y = screenY - lineHeightAndMargin;
		this.rootContainer.width = offsetWidth;
		this.rootContainer.height = ticketBoundsHeight + 2 * lineHeightAndMargin;

		this.topLineAndTexts.y = 0;
		this.bottomLineAndTexts.y = lineHeightAndMargin + ticketBoundsHeight;

		const leftText = this.ticketCountText(ticketsOffScreen.left, true);
		const rightText = this.ticketCountText(ticketsOffScreen.right, false);

		this.topLineAndTexts.left = leftText;
		this.topLineAndTexts.right = rightText;
		this.bottomLineAndTexts.left = leftText;
		this.bottomLineAndTexts.right = rightText;

		this.topLineAndTexts.updateLayout();
		this.bottomLineAndTexts.updateLayout();

		this.parentApplication.markAsDirty();
	}

	public setVisible(visible: boolean) {
		this.rootContainer.visible = visible;
		this.parentApplication.markAsDirty();
	}

	private ticketCountText(count: number, left: boolean) {
		if (count <= 0) {
			return '';
		}
		if (count === 1) {
			return left
				? strings.WARNING_DRAGGED_TICKETS_OFF_LEFT_SINGULAR
				: strings.WARNING_DRAGGED_TICKETS_OFF_RIGHT_SINGULAR;
		}
		const stringFunc = left
			? strings.WARNING_DRAGGED_TICKETS_OFF_LEFT_PLURAL
			: strings.WARNING_DRAGGED_TICKETS_OFF_RIGHT_PLURAL;
		return stringFunc(count);
	}

	public destroy(opts?: any): void {
		this.topLineAndTexts.destroy();
		this.bottomLineAndTexts.destroy();
		this.topLineAndTexts = null;
		this.bottomLineAndTexts = null;
		this.rootContainer = null;
	}
}
